// Youtu-Agent Integration Service for OilBuddy AI
// This service connects our Vue.js frontend with the Youtu-agent Python framework

export interface YoutuAgentConfig {
  name: string
  description: string
  goal: string
  tools: string[]
  model: string
  systemPrompt: string
}

export interface AgentExecutionResult {
  success: boolean
  steps: ExecutionStep[]
  finalOutput: any
  executionTime: number
  cost: number
}

export interface ExecutionStep {
  stepNumber: number
  description: string
  tool?: string
  args?: any
  result?: any
  success: boolean
  error?: string
  executionTime: number
}

export interface OilfieldAgent {
  id: string
  name: string
  type: 'safety' | 'financial' | 'operations' | 'compliance' | 'custom'
  description: string
  capabilities: string[]
  tools: string[]
  status: 'active' | 'inactive' | 'training'
  performance: {
    accuracy: number
    responseTime: number
    tasksCompleted: number
    costSavings: number
  }
}

// Pre-configured Oilfield Agents
export const OILFIELD_AGENTS: Record<string, OilfieldAgent> = {
  safety: {
    id: 'safety-agent',
    name: 'Safety Compliance Agent',
    type: 'safety',
    description: 'Monitors and ensures safety compliance across all operations',
    capabilities: ['incident_analysis', 'compliance_checking', 'safety_training', 'risk_assessment'],
    tools: ['checkCompliance', 'generateReport', 'searchJobs', 'analyzeIncident'],
    status: 'active',
    performance: {
      accuracy: 98,
      responseTime: 1.2,
      tasksCompleted: 0,
      costSavings: 0
    }
  },
  
  financial: {
    id: 'financial-agent',
    name: 'Financial Management Agent',
    type: 'financial',
    description: 'Handles invoicing, payroll, tax optimization, and financial planning',
    capabilities: ['tax_optimization', 'expense_tracking', 'payroll_management', 'cost_analysis'],
    tools: ['createInvoice', 'generateReport', 'searchJobs', 'analyzeExpenses'],
    status: 'active',
    performance: {
      accuracy: 95,
      responseTime: 1.5,
      tasksCompleted: 0,
      costSavings: 0
    }
  },
  
  operations: {
    id: 'operations-agent',
    name: 'Operations Optimization Agent',
    type: 'operations',
    description: 'Optimizes job scheduling, resource allocation, and operational efficiency',
    capabilities: ['scheduling_optimization', 'resource_allocation', 'performance_analysis', 'efficiency_improvement'],
    tools: ['scheduleJob', 'searchJobs', 'generateReport', 'analyzePerformance'],
    status: 'active',
    performance: {
      accuracy: 92,
      responseTime: 2.1,
      tasksCompleted: 0,
      costSavings: 0
    }
  },
  
  compliance: {
    id: 'compliance-agent',
    name: 'Regulatory Compliance Agent',
    type: 'compliance',
    description: 'Ensures regulatory compliance and manages documentation',
    capabilities: ['regulatory_monitoring', 'documentation_management', 'audit_preparation', 'compliance_reporting'],
    tools: ['checkCompliance', 'generateReport', 'searchJobs', 'trackRegulations'],
    status: 'active',
    performance: {
      accuracy: 97,
      responseTime: 1.8,
      tasksCompleted: 0,
      costSavings: 0
    }
  }
}

class YoutuAgentIntegration {
  private baseUrl: string = '/api/ai/agents'  // Use AI agents API which delegates to Youtu-agent
  private activeAgents: Map<string, OilfieldAgent> = new Map()

  constructor() {
    this.initializeAgents()
  }

  private initializeAgents() {
    Object.values(OILFIELD_AGENTS).forEach(agent => {
      this.activeAgents.set(agent.id, agent)
    })
  }

  // Create a new agent using AI agents API (which delegates to Youtu-agent)
  async createAgent(config: YoutuAgentConfig): Promise<OilfieldAgent> {
    try {
      const response = await fetch(`${this.baseUrl}/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || 'demo_token'}`
        },
        body: JSON.stringify({
          name: config.name,
          description: config.description,
          goal: config.goal,
          tools: config.tools,
          aiModel: config.model,
          systemPrompt: config.systemPrompt,
          type: this.determineAgentType(config)
        })
      })

      if (response.ok) {
        const result = await response.json()
        if (result.success && result.agent) {
          const agent = result.agent
          // Convert AI agents API response to OilfieldAgent format
          const oilfieldAgent: OilfieldAgent = {
            id: agent.id,
            name: agent.name,
            description: agent.description,
            type: agent.agent_type || 'youtu-agent',
            capabilities: agent.capabilities || agent.tools || [],
            tools: agent.tools || agent.capabilities || [],
            status: agent.status,
            performance: agent.performance || {
              accuracy: 95,
              responseTime: 1.2,
              tasksCompleted: 0,
              costSavings: 0
            }
          }
          this.activeAgents.set(agent.id, oilfieldAgent)
          return oilfieldAgent
        } else {
          throw new Error(result.error || 'Failed to create agent')
        }
      } else {
        throw new Error('Failed to create agent')
      }
    } catch (error) {
      console.error('Error creating agent:', error)
      // Return a mock agent for demo purposes
      return this.createMockAgent(config)
    }
  }

  // Determine agent type based on configuration
  private determineAgentType(config: YoutuAgentConfig): string {
    const prompt = config.systemPrompt?.toLowerCase() || ''
    const name = config.name?.toLowerCase() || ''
    const description = config.description?.toLowerCase() || ''
    
    if (prompt.includes('safety') || name.includes('safety') || description.includes('safety')) {
      return 'safety'
    }
    if (prompt.includes('document') || name.includes('document') || description.includes('document')) {
      return 'document'
    }
    if (prompt.includes('schedule') || name.includes('schedule') || description.includes('schedule')) {
      return 'scheduler'
    }
    if (prompt.includes('financial') || name.includes('financial') || description.includes('financial')) {
      return 'financial'
    }
    
    return 'default'
  }

  private createMockAgent(config: YoutuAgentConfig): OilfieldAgent {
    return {
      id: `agent-${Date.now()}`,
      name: config.name,
      type: 'custom',
      description: config.description,
      capabilities: ['task_execution', 'tool_usage', 'reasoning'],
      tools: config.tools,
      status: 'active',
      performance: {
        accuracy: 90,
        responseTime: 1.5,
        tasksCompleted: 0,
        costSavings: 0
      }
    }
  }

  // Execute a goal with an agent via AI agents API (which delegates to Youtu-agent)
  async executeGoal(agentId: string, goal: string, context?: any): Promise<AgentExecutionResult> {
    try {
      const response = await fetch(`${this.baseUrl}/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || 'demo_token'}`
        },
        body: JSON.stringify({
          agentId,
          goal,
          context: context || {}
        })
      })

      if (response.ok) {
        return await response.json()
      } else {
        throw new Error('Failed to execute goal')
      }
    } catch (error) {
      console.error('Error executing goal:', error)
      // Return mock execution result for demo
      return this.createMockExecutionResult(agentId, goal)
    }
  }

  private createMockExecutionResult(agentId: string, goal: string): AgentExecutionResult {
    const agent = this.activeAgents.get(agentId)
    const steps: ExecutionStep[] = []

    // Generate mock execution steps based on agent type
    if (agent?.type === 'safety') {
      steps.push(
        {
          stepNumber: 1,
          description: 'Analyzing safety requirements',
          success: true,
          executionTime: 1200
        },
        {
          stepNumber: 2,
          description: 'Checking compliance status',
          tool: 'checkCompliance',
          args: { contractorId: 'demo-contractor' },
          success: true,
          executionTime: 800
        },
        {
          stepNumber: 3,
          description: 'Generating safety report',
          tool: 'generateReport',
          args: { reportType: 'safety', data: {} },
          success: true,
          executionTime: 1500
        }
      )
    } else if (agent?.type === 'financial') {
      steps.push(
        {
          stepNumber: 1,
          description: 'Analyzing financial requirements',
          success: true,
          executionTime: 1000
        },
        {
          stepNumber: 2,
          description: 'Creating invoice',
          tool: 'createInvoice',
          args: { timesheetId: 'demo-timesheet' },
          success: true,
          executionTime: 1200
        },
        {
          stepNumber: 3,
          description: 'Generating financial report',
          tool: 'generateReport',
          args: { reportType: 'financial', data: {} },
          success: true,
          executionTime: 1800
        }
      )
    } else {
      // Generic execution for other agent types
      steps.push(
        {
          stepNumber: 1,
          description: 'Analyzing goal requirements',
          success: true,
          executionTime: 1000
        },
        {
          stepNumber: 2,
          description: 'Executing primary task',
          success: true,
          executionTime: 2000
        },
        {
          stepNumber: 3,
          description: 'Generating final output',
          success: true,
          executionTime: 800
        }
      )
    }

    return {
      success: true,
      steps,
      finalOutput: {
        summary: `Successfully completed goal: ${goal}`,
        agentType: agent?.type,
        recommendations: ['Continue monitoring', 'Schedule follow-up review']
      },
      executionTime: steps.reduce((total, step) => total + step.executionTime, 0),
      cost: 0.05 // Mock cost
    }
  }

  // Get available agents
  async getAgents(): Promise<OilfieldAgent[]> {
    try {
      const response = await fetch(`${this.baseUrl}/agents`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        return await response.json()
      } else {
        // Return mock agents if API is unavailable
        return Array.from(this.activeAgents.values())
      }
    } catch (error) {
      console.error('Error fetching agents:', error)
      return Array.from(this.activeAgents.values())
    }
  }

  // Get agent by ID
  async getAgent(agentId: string): Promise<OilfieldAgent | null> {
    try {
      const response = await fetch(`${this.baseUrl}/agents/${agentId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        return await response.json()
      } else {
        return this.activeAgents.get(agentId) || null
      }
    } catch (error) {
      console.error('Error fetching agent:', error)
      return this.activeAgents.get(agentId) || null
    }
  }

  // Update agent performance
  async updateAgentPerformance(agentId: string, performance: Partial<OilfieldAgent['performance']>): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/agents/${agentId}/performance`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(performance)
      })

      if (response.ok) {
        const agent = this.activeAgents.get(agentId)
        if (agent) {
          agent.performance = { ...agent.performance, ...performance }
        }
        return true
      } else {
        return false
      }
    } catch (error) {
      console.error('Error updating agent performance:', error)
      return false
    }
  }

  // Get execution history
  async getExecutionHistory(agentId?: string): Promise<AgentExecutionResult[]> {
    try {
      const url = agentId ? `${this.baseUrl}/history/${agentId}` : `${this.baseUrl}/history`
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        return await response.json()
      } else {
        return []
      }
    } catch (error) {
      console.error('Error fetching execution history:', error)
      return []
    }
  }


}

// Export singleton instance
export const youtuAgentIntegration = new YoutuAgentIntegration()
